import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

export interface ActionDialogReason {
  label: string;
  required: boolean;
  minLength?: number;
}

interface ActionDialogProps {
  open: boolean;
  title: string;
  /** Описание последствий действия — показывается под заголовком. */
  description?: string;
  confirmLabel: string;
  confirmColor?: 'primary' | 'error' | 'warning' | 'success';
  /** Если задано — диалог содержит поле причины/комментария. */
  reason?: ActionDialogReason;
  onConfirm: (reason: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Модальный диалог подтверждения действия модератора с опциональным полем
 * причины. Замена window.prompt (см. 02_ADMIN_ARCHITECTURE.md §4.4).
 */
export function ActionDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmColor = 'primary',
  reason,
  onConfirm,
  onClose,
}: ActionDialogProps): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const minLength = reason?.minLength ?? (reason?.required ? 1 : 0);
  const tooShort = reason !== undefined && reason.required && text.trim().length < minLength;

  function handleClose(): void {
    if (busy) return;
    setText('');
    onClose();
  }

  async function handleConfirm(): Promise<void> {
    setBusy(true);
    try {
      await onConfirm(text.trim());
      setText('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description ? <DialogContentText>{description}</DialogContentText> : null}
        {reason ? (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            margin="dense"
            label={reason.label}
            value={text}
            onChange={(e) => setText(e.target.value)}
            helperText={
              reason.required && minLength > 1
                ? `Обязательно, минимум ${minLength} символов`
                : reason.required
                  ? 'Обязательное поле'
                  : 'Необязательно'
            }
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy} color="inherit">
          Отмена
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          disabled={busy || tooShort}
          color={confirmColor}
          variant="contained"
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
